import React, { useCallback } from "react";
import { Button, Dropdown, Menu } from "antd";
import { Link, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCogs,
  faEdit,
  faEllipsis,
  faGift,
  faLayerGroup,
  faTrophy,
} from "@fortawesome/free-solid-svg-icons";

import SmartRtpTablePage from "./components/SmartRtpTablePage";
import { AID_STATUS_TABS } from "./config";
import {
  getDashboardSummary,
  getGamesList,
} from "../../../controllers/V1/smartRtp/smartRtpController";
import { usePermissions } from "../../../controllers/PermissionContext";
import { formatSmartRtpNumber } from "../../../utils/bigMath";

function GameActionsMenu({ row }) {
  const navigate = useNavigate();
  const gameId = row?._id;
  const poolId =
    row?.prize_pool_id && typeof row.prize_pool_id === "object"
      ? row.prize_pool_id._id
      : row?.prize_pool_id || null;

  return (
    <Dropdown
      placement="bottomLeft"
      arrow
      overlay={
        <Menu>
          <Menu.Item
            key="edit"
            onClick={() => navigate(`/smart-rtp/games/addeditdata?editId=${gameId}`)}
          >
            <FontAwesomeIcon icon={faEdit} />{" "}
            <span className="show_span">Edit Game</span>
          </Menu.Item>
          <Menu.Item
            key="pool"
            onClick={() =>
              navigate(
                poolId
                  ? `/smart-rtp/pools/addeditdata?editId=${poolId}`
                  : `/smart-rtp/pools/addeditdata?gameId=${gameId}`
              )
            }
          >
            <FontAwesomeIcon icon={faTrophy} />{" "}
            <span className="show_span">Pool</span>
          </Menu.Item>
          <Menu.Item
            key="slabs"
            onClick={() => navigate(`/smart-rtp/slabs/addeditdata?gameId=${gameId}`)}
          >
            <FontAwesomeIcon icon={faLayerGroup} />{" "}
            <span className="show_span">Slabs</span>
          </Menu.Item>
          <Menu.Item
            key="buyer-reward"
            onClick={() =>
              navigate(`/smart-rtp/buyer-reward/addeditdata?gameId=${gameId}`)
            }
          >
            <FontAwesomeIcon icon={faGift} />{" "}
            <span className="show_span">Buyer Reward</span>
          </Menu.Item>
          <Menu.Item
            key="settings"
            onClick={() => navigate(`/smart-rtp/settings/addeditdata?gameId=${gameId}`)}
          >
            <FontAwesomeIcon icon={faCogs} />{" "}
            <span className="show_span">Settings</span>
          </Menu.Item>
        </Menu>
      }
    >
      <FontAwesomeIcon icon={faEllipsis} style={{ cursor: "pointer" }} />
    </Dropdown>
  );
}

export default function SmartRtpGamesPage() {
  const PERMISSION = usePermissions("smartRtp");
  const canAddEdit = PERMISSION?.fullAccess === "Y" || PERMISSION?.addEdit === "Y";
  const canManageGameConfig =
    PERMISSION?.fullAccess === "Y" ||
    PERMISSION?.list === "Y" ||
    PERMISSION?.view === "Y" ||
    PERMISSION?.addEdit === "Y";

  const fetchGamesWithTodaySummary = useCallback(async (options = {}) => {
    const response = await getGamesList(options);
    if (response?.status !== true || !Array.isArray(response.result)) {
      return response;
    }

    const games = await Promise.all(
      response.result.map(async (game) => {
        if (!game?._id) return game;
        const summaryResponse = await getDashboardSummary({ game_id: game._id });
        const metrics = summaryResponse?.result?.metrics || {};
        return {
          ...game,
          current_sale: metrics.total_revenue || 0,
          current_rtp_distributed: metrics.rtp_distributed_amount || 0,
          current_rtp_distributed_percent:
            metrics.rtp_distributed_percent ?? metrics.actual_rtp ?? 0,
        };
      })
    );

    return { ...response, result: games };
  }, []);

  return (
    <SmartRtpTablePage
      title="Smart RTP Games"
      activeKey="games"
      fetchData={fetchGamesWithTodaySummary}
      statusTabs={AID_STATUS_TABS}
      showFilter={false}
      showExport={false}
      columns={[
        { key: "title", label: "Title" },
        // { key: "prize_title", label: "Prize Title" },
        // { key: "game_code", label: "Game Code" },
        { key: "ticket_price", label: "Ticket Price", render: (row) => formatSmartRtpNumber(row?.ticket_price) },
        {
          key: "current_sale",
          label: "Current Sale",
          render: (row) => formatSmartRtpNumber(row?.current_sale),
          exportValue: (row) => row?.current_sale || 0,
        },
        {
          key: "current_rtp_distributed",
          label: "RTP Distributed (%)",
          render: (row) => {
            const amount = formatSmartRtpNumber(row?.current_rtp_distributed);
            const pct = formatSmartRtpNumber(row?.current_rtp_distributed_percent);
            return `${amount} (${pct}%)`;
          },
          exportValue: (row) => {
            const amount = row?.current_rtp_distributed || 0;
            const pct = row?.current_rtp_distributed_percent || 0;
            return `${amount} (${pct}%)`;
          },
        },
        {
          key: "game_wise_rtp_target",
          label: "Target RTP %",
          render: (row) => formatSmartRtpNumber(row?.game_wise_rtp_target),
        },
        { key: "coupon_selection_type", label: "Coupon Selection" },
        { key: "slots_per_ticket", label: "Slots / Ticket" },
        // { key: "number_repeat", label: "Number Repeat" },
        
        { key: "status", label: "Status" },
        ...(canAddEdit
          ? [
              {
                key: "actions",
                label: "Actions",
                render: (row) => <GameActionsMenu row={row} />,
              },
            ]
          : []),
      ]}
      actions={
        canAddEdit || canManageGameConfig ? (
          <div className="d-flex flex-wrap gap-2">
            {canManageGameConfig ? (
              <>
                <Link to="/smart-rtp/pools/addeditdata">
                  <Button icon={<FontAwesomeIcon icon={faTrophy} />}>Pool</Button>
                </Link>
                <Link to="/smart-rtp/settings/addeditdata">
                  <Button icon={<FontAwesomeIcon icon={faCogs} />}>Settings</Button>
                </Link>
                <Link to="/smart-rtp/slabs/addeditdata">
                  <Button icon={<FontAwesomeIcon icon={faLayerGroup} />}>Slab</Button>
                </Link>
                <Link to="/smart-rtp/buyer-reward/addeditdata">
                  <Button icon={<FontAwesomeIcon icon={faGift} />}>Buyer Reward</Button>
                </Link>
              </>
            ) : null}
            {canAddEdit ? (
              <Link to="/smart-rtp/games/addeditdata">
                <Button type="primary">Add Game</Button>
              </Link>
            ) : null}
          </div>
        ) : null
      }
    />
  );
}
